/**
 * The project's task registry as the window reads it (RFC 0319 3e).
 *
 * The registry is a map on the project doc, which every surface already holds,
 * so these reads open nothing: a backlink is a filter over refs.
 */

import { registryTasks, type TaskSummary } from '@mod/core/model'

// Moved to core so the voice reads the same registry (RFC 0307).
export {
  registryTasks,
  sessionTaskGroups,
  type TaskGroup,
} from '@mod/core/model'

/** Pending work no session has taken, ordered for the new-pane page (0304 3c). */
export function unownedTasks(projectDoc: unknown): TaskSummary[] {
  const priority = { critical: 0, high: 1, normal: 2, low: 3 } as const
  return registryTasks(projectDoc)
    .filter((task) => task.status === 'pending')
    .sort(
      (a, b) =>
        priority[a.priority ?? 'normal'] - priority[b.priority ?? 'normal'] ||
        a.updatedAt.localeCompare(b.updatedAt) ||
        a.id.localeCompare(b.id),
    )
}

/**
 * What is open in a whole project, and how much of it waits on you (RFC 0319 3d).
 *
 * Not done over total: a project never ends, so its total only grows and the
 * fraction says nothing about what needs doing now. Waiting is a blocked task
 * with nothing blocking it, which is a question for a person. Null when nothing
 * is open, so a caller draws no count rather than a 0.
 */
export function projectTaskCount(
  projectDoc: unknown,
): { open: number; waiting: number } | null {
  const open = registryTasks(projectDoc).filter(
    (t) =>
      t.status === 'pending' || t.status === 'active' || t.status === 'blocked',
  )
  if (!open.length) return null
  return {
    open: open.length,
    waiting: open.filter(
      (t) => t.status === 'blocked' && t.blockedBy.length === 0,
    ).length,
  }
}

/** The tasks whose document refs name this document: its backlinks. */
export function tasksNaming(
  projectDoc: unknown,
  documentId: string | readonly string[],
): TaskSummary[] {
  const targets = new Set(
    typeof documentId === 'string' ? [documentId] : documentId,
  )
  if (targets.size === 0) return []
  return registryTasks(projectDoc).filter((t) =>
    t.refs.some((r) => r.type === 'document' && targets.has(r.target)),
  )
}

/**
 * A document's task backlinks plus every descendant beneath them.
 *
 * Subtasks inherit the work's context from their parent and therefore do not
 * need to repeat the document ref. Keep the projection parent-first so every
 * task surface can draw the hierarchy without opening the task documents.
 */
export function documentTaskTree(
  projectDoc: unknown,
  documentId: string,
): TaskSummary[] {
  if (!documentId) return []
  const all = registryTasks(projectDoc)
  const roots = all.filter((task) =>
    task.refs.some(
      (ref) => ref.type === 'document' && ref.target === documentId,
    ),
  )
  const children = new Map<string, TaskSummary[]>()
  for (const task of all) {
    if (!task.parentId) continue
    const siblings = children.get(task.parentId) ?? []
    siblings.push(task)
    children.set(task.parentId, siblings)
  }

  const projected: TaskSummary[] = []
  const visited = new Set<string>()
  const append = (task: TaskSummary) => {
    if (visited.has(task.id)) return
    visited.add(task.id)
    projected.push(task)
    for (const child of children.get(task.id) ?? []) append(child)
  }
  for (const root of roots) append(root)
  return projected
}
